import { useEffect, useRef } from 'react';

import {
  fetchGetProductOffers,
  fetchPostApplicationDecline,
  fetchPostFormalOffers,
} from '@iwoca/lapi-client/edge';

import { useGetApplication } from '../../../api/lending/lapiHooks';
import { useStateKey } from '../../../hooks/useStateKey.hook';
import {
  checkStatusIsDeclined,
  getApprovalRequirementErrors,
  getIwocaPayProductOffers,
  getProductDeclineUid,
  getSuggestedProduct,
} from '../Dashboard.helpers';

export function useApplicationProcessor({
  applicationStatus,
}: {
  applicationStatus?: string;
}) {
  const hasProcessed = useRef(false);
  const { stateKey } = useStateKey();
  const { fetchApplication } = useGetApplication();

  useEffect(() => {
    if (hasProcessed.current) return;
    if (applicationStatus !== 'open') return;
    if (!stateKey) return;

    const processApplication = async () => {
      hasProcessed.current = true;

      try {
        // Fetch product offers
        const productOffersResponse = await fetchGetProductOffers({
          stateKey,
        });
        const productOffers = productOffersResponse.data;

        if (!productOffers) return;

        const suggestedProduct = getSuggestedProduct(productOffers);

        if (!suggestedProduct) return;

        // Check for approval requirement errors
        const approvalRequirementErrors =
          getApprovalRequirementErrors(suggestedProduct);
        if (Array.from(approvalRequirementErrors.values()).length) {
          return;
        }

        // Check if declined
        const isDeclined = checkStatusIsDeclined(suggestedProduct);

        if (isDeclined) {
          const productDeclineUid = getProductDeclineUid(suggestedProduct);
          const declineStatus =
            suggestedProduct.status === 'hard_declined'
              ? 'Hard Declined'
              : 'Soft Declined';

          await fetchPostApplicationDecline({
            stateKey,
            body: {
              data: {
                status: declineStatus,
                product_type_decline_uid: productDeclineUid!,
              },
            },
          });

          // Refetch application after decline
          await fetchApplication();
          return;
        }

        // Formalise offers if available
        const iwocaPayProductOffers = getIwocaPayProductOffers(productOffers);

        if (iwocaPayProductOffers && iwocaPayProductOffers.length > 0) {
          await fetchPostFormalOffers({
            stateKey,
            body: {
              data: {
                offer_ids: iwocaPayProductOffers.map((offer) => offer.id),
              },
            },
          });

          // Refetch application after formalisation
          await fetchApplication();
        }
      } catch {
        // Silently handle errors - the dashboard will show the current state
      }
    };

    processApplication();
  }, [applicationStatus, stateKey, fetchApplication]);
}
